import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles, Star } from "lucide-react";
import dashboardImg from "@/assets/landing-dashboard.jpg";

interface HeroSectionProps {
  onCTA: () => void;
}

export const HeroSection = ({ onCTA }: HeroSectionProps) => {
  // Inject preload for LCP image with the Vite-resolved hashed URL so the
  // browser discovers it immediately, addressing LCP request discovery.
  useEffect(() => {
    const existing = document.head.querySelector(
      `link[rel="preload"][href="${dashboardImg}"]`
    );
    if (existing) return;
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = dashboardImg;
    link.setAttribute("fetchpriority", "high");
    document.head.appendChild(link);
  }, []);
  return (
    <section className="container mx-auto px-4 sm:px-6 lg:px-8 pt-8 lg:pt-12">
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-50 via-card to-primary/5 ring-1 ring-emerald-200/60 shadow-card">
        {/* Decorative blobs */}
        <div className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-emerald-300/30 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-primary/20 blur-3xl" />

        <div className="relative grid lg:grid-cols-2 gap-12 lg:gap-8 items-center p-8 lg:p-14">
          {/* Left */}
          <div className="space-y-7">
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3.5 py-1.5">
              <Sparkles className="h-3.5 w-3.5 text-emerald-600" />
              <span className="text-[11px] font-bold tracking-widest text-emerald-700 uppercase">
                The AI-Powered Trading Journal
              </span>
            </div>

            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-foreground leading-[1.05]">
              Trade Less.
              <br />
              Understand <span className="text-emerald-600">More.</span>
            </h1>

            <p className="text-lg sm:text-xl text-foreground/80 leading-relaxed max-w-xl font-medium">
              JouRex is the operating system built for serious traders — a single,
              intelligent workspace where your strategy, risk, behavior and execution
              come together as one disciplined process. It transforms raw market activity
              into structured insight, turning every decision you make into measurable
              progress and every session into a step toward becoming a more consistent,
              accountable, and professional trader.
            </p>

            <div className="pt-2">
              <Button
                onClick={onCTA}
                size="lg"
                className="h-14 px-8 rounded-xl gradient-primary text-primary-foreground font-semibold text-base shadow-elevated hover:scale-[1.02] transition-transform"
              >
                JouRex Trading Center
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>

            {/* Trust indicator */}
            <div className="flex items-center gap-4 pt-4">
              <div className="flex -space-x-2">
                {[
                  "from-rose-400 to-rose-600",
                  "from-amber-400 to-amber-600",
                  "from-emerald-400 to-emerald-600",
                  "from-blue-400 to-blue-600",
                ].map((g, i) => (
                  <div
                    key={i}
                    className={`h-9 w-9 rounded-full border-2 border-background bg-gradient-to-br ${g}`}
                  />
                ))}
              </div>
              <div>
                <div className="flex items-center gap-0.5 mb-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="text-sm text-muted-foreground font-medium">
                  Trusted by 25,000+ traders worldwide
                </p>
              </div>
            </div>
          </div>

          {/* Right — dashboard mockup */}
          <div className="relative lg:pl-4">
            <div className="relative rounded-2xl overflow-hidden shadow-elevated ring-1 ring-border/50 bg-card">
              <img
                src={dashboardImg}
                alt="JouRex trading analytics dashboard showing performance, win rate and discipline score"
                className="w-full h-auto block"
                width={1100}
                height={738}
                fetchPriority="high"
                loading="eager"
                decoding="async"
              />
            </div>
            {/* Floating glow */}
            <div className="pointer-events-none absolute -inset-6 -z-10 bg-gradient-to-tr from-emerald-400/20 via-transparent to-primary/20 blur-3xl rounded-3xl" />
          </div>
        </div>
      </div>
    </section>
  );
};
